import { faker } from "@faker-js/faker/locale/nl";
import { env } from "../config/env";

interface DebugField {
  name: string;
  type: string;
  required: boolean;
}

// Helper function to generate a random image file
const generateRandomFile = async (): Promise<File> => {
  const canvas = document.createElement("canvas");
  canvas.width = 320;
  canvas.height = 240;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas context");

  // Fill with random color
  ctx.fillStyle = faker.color.rgb();
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Draw some random shapes
  ctx.fillStyle = faker.color.rgb();
  ctx.beginPath();
  ctx.arc(
    faker.number.int({ min: 100, max: 700 }),
    faker.number.int({ min: 100, max: 500 }),
    faker.number.int({ min: 50, max: 150 }),
    0,
    Math.PI * 2,
  );
  ctx.fill();

  // Add some random text
  ctx.fillStyle = "#000000";
  ctx.font = "24px Arial";
  ctx.fillText(faker.lorem.words(3), 50, 50);

  // Convert to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not create blob"));
          return;
        }
        // Release canvas memory after blob creation
        canvas.width = 0;
        canvas.height = 0;
        resolve(
          new File([blob], `debug-image-${faker.string.uuid()}.jpg`, {
            type: "image/jpeg",
          }),
        );
      },
      "image/jpeg",
      0.75,
    );
  });
};

// Helper function to generate a random signature
const generateRandomSignature = async (): Promise<string> => {
  const canvas = document.createElement("canvas");
  canvas.width = 300;
  canvas.height = 100;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas context");

  // Clear canvas
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Draw signature
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 2;
  ctx.beginPath();

  // Start point
  let x = 50;
  let y = 50;

  // Draw random squiggles
  for (let i = 0; i < 10; i++) {
    ctx.moveTo(x, y);
    x += faker.number.int({ min: 10, max: 30 });
    y += faker.number.int({ min: -20, max: 20 });
    ctx.lineTo(x, y);
  }

  ctx.stroke();

  // Convert to base64
  return canvas.toDataURL("image/png");
};

// Helper function to generate value for a field
const generateFieldValue = (fieldName: string): string => {
  // Remove prefixes for field type detection
  const baseField = fieldName.replace(/^(new_|return_)/, "");

  switch (baseField) {
    case "date":
      return faker.date.recent().toISOString().split("T")[0];
    case "time":
      return faker.date
        .recent()
        .toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
    case "driver_name":
    case "customer_name":
      return faker.person.fullName();
    case "license_number":
      return faker.vehicle.vrm();
    case "mileage":
      return faker.number.int({ min: 0, max: 200000 }).toString();
    case "key_count":
      return faker.number.int({ min: 1, max: 3 }).toString();
    case "address":
      return faker.location.streetAddress();
    case "damage":
      return faker.lorem.paragraph();
    default:
      return faker.lorem.word();
  }
};

export const generateRandomFormData = async (
  fields: DebugField[],
): Promise<Record<string, any>> => {
  const formData: Record<string, any> = {};
  const photos: Record<string, File[]> = {};
  const signatures: Record<string, string> = {};

  for (const field of fields) {
    if (field.type === "file") {
      try {
        // Keep debug payload small to avoid memory pressure in browsers.
        const numImages = faker.number.int({ min: 1, max: 3 });
        const files: File[] = [];
        for (let i = 0; i < numImages; i++) {
          files.push(await generateRandomFile());
        }
        photos[field.name] = files;
      } catch (error) {
        console.error(`Error generating files for ${field.name}:`, error);
      }
    } else if (field.type === "signature") {
      try {
        const signature = await generateRandomSignature();
        signatures[field.name] = signature;
      } catch (error) {
        console.error(`Error generating signature for ${field.name}:`, error);
      }
    } else {
      formData[field.name] = generateFieldValue(field.name);
    }
  }

  return {
    ...formData,
    photos,
    signatures,
  };
};

// Check if debug mode is enabled
export const isDebugMode = (): boolean => {
  return env.debug;
};
